import { Link } from 'react-router-dom'
import { CONTACT, ENTITY, PLACE } from '../constants'
import { StationMark } from './StationMark'

type NavItem = { label: string; href?: string; to?: string }

const buildHome: NavItem[] = [
  { href: '#legend', label: 'Custom product engineering' },
  { href: '#legend', label: 'AI-assisted delivery' },
  { href: '#legend', label: 'Systems integration' },
  { to: '/compound', label: 'Compound' },
]

const buildCompound: NavItem[] = [
  { to: '/', label: 'Custom software development' },
  { to: '/compound', label: 'Compound' },
]

const buildLegal: NavItem[] = [
  { to: '/', label: 'Home' },
  { to: '/compound', label: 'Compound' },
]

const sheetHome: NavItem[] = [
  { href: '#register', label: 'Selected engagements' },
  { href: '#traverse', label: 'How we work' },
  { href: '#models', label: 'Engagement models' },
  { href: '#notes', label: 'How we use AI' },
  { href: '#authority', label: 'Why Passwork' },
  { to: '/compound', label: 'Compound' },
]

const sheetCompound: NavItem[] = [
  { href: '#capabilities', label: 'What Compound runs' },
  { href: '#industry', label: 'Industry currency' },
  { href: '#memory', label: 'Four kinds of memory' },
  { href: '#register', label: 'Selected cases' },
  { href: '#traverse', label: 'How Compound runs' },
  { href: '#models', label: 'Subscription plans' },
  { href: '#notes', label: 'What Compound is not' },
  { to: '/', label: 'Custom development' },
]

const sheetLegal: NavItem[] = [
  { to: '/terms', label: 'Terms of use' },
  { to: '/privacy', label: 'Privacy policy' },
  { to: '/cookies', label: 'Cookie notice' },
]

const legalBar: NavItem[] = [
  { to: '/terms', label: 'Terms' },
  { to: '/privacy', label: 'Privacy' },
  { to: '/cookies', label: 'Cookies' },
]

type SheetMarginProps = {
  variant?: 'home' | 'compound' | 'legal'
}

function NavLink({ item }: { item: NavItem }) {
  const className =
    'index-link text-[0.9375rem] text-ink-muted transition-colors hover:text-ink'

  if (item.to) {
    return (
      <Link to={item.to} className={className}>
        {item.label}
      </Link>
    )
  }

  return (
    <a href={item.href} className={className}>
      {item.label}
    </a>
  )
}

/** Sheet margin — legal bar and index. Stays on sheet ground; ink is for lettering. */
export function SheetMargin({ variant = 'home' }: SheetMarginProps) {
  const sheet =
    variant === 'compound'
      ? sheetCompound
      : variant === 'legal'
        ? sheetLegal
        : sheetHome
  const buildLinks =
    variant === 'compound'
      ? buildCompound
      : variant === 'legal'
        ? buildLegal
        : buildHome

  const blurb =
    variant === 'compound'
      ? 'Compound — engineering method that compounds on your codebase. Built for teams working in Cursor.'
      : variant === 'legal'
        ? 'Legal sheets for passwork.ae, custom engagements, and the Compound subscription.'
        : 'Custom software development with AI. We survey the terrain, build the system, and hand it back documented.'

  return (
    <footer className="border-t-2 border-ink bg-sheet">
      <div className="mx-auto max-w-[100rem] px-6 py-12 lg:px-8">
        <div className="grid gap-10 md:grid-cols-[1.4fr_1fr_1fr] lg:gap-16">
          <div>
            <Link to="/" className="flex items-center gap-2.5 text-ink">
              <StationMark className="h-7 w-7 shrink-0 text-overprint" />
              <span
                className="text-xl font-bold uppercase tracking-[0.02em]"
                style={{ fontStretch: '76%' }}
              >
                Passwork
              </span>
            </Link>
            <p className="mt-4 max-w-sm text-base leading-relaxed text-ink-muted">
              {blurb}
            </p>
            <a
              href={`mailto:${CONTACT}`}
              className="index-link mt-5 inline-block text-base font-medium text-ink"
            >
              {CONTACT}
            </a>
          </div>

          <nav aria-label="Products and practices">
            <h2 className="cell-label text-ink-muted">
              {variant === 'legal'
                ? 'Site'
                : variant === 'compound'
                  ? 'Products'
                  : 'What we build'}
            </h2>
            <ul className="mt-4 space-y-2.5">
              {buildLinks.map((item) => (
                <li key={item.label}>
                  <NavLink item={item} />
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-label="Sheet index">
            <h2 className="cell-label text-ink-muted">
              {variant === 'legal' ? 'Legal' : 'Sheet index'}
            </h2>
            <ul className="mt-4 space-y-2.5">
              {sheet.map((item) => (
                <li key={item.label}>
                  <NavLink item={item} />
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <div className="mt-10 flex flex-col gap-3 border-t border-rule pt-5 sm:flex-row sm:items-center sm:justify-between">
          <p className="datum text-ink-muted">
            {ENTITY} — {PLACE}
          </p>
          <nav
            aria-label="Legal"
            className="flex flex-wrap items-center gap-x-5 gap-y-2"
          >
            {legalBar.map((item) => (
              <NavLink key={item.label} item={item} />
            ))}
            <span className="datum text-ink-muted">
              © 2026 Passwork Software Solution
            </span>
          </nav>
        </div>
      </div>
    </footer>
  )
}
